import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";

// A native scrollbar is painted as part of its scrolling box, so when the page
// scrolls past a tall board the bar leaves the screen with it — it can't be
// made sticky. The rail therefore hides its own scrollbar and this thin sticky
// proxy carries the always-visible one: a spacer matching the rail's
// scrollWidth, scroll-synced with the rail in both directions (value-compare
// guards the feedback loop). On mobile it sits above the fixed BottomTabBar.
const StickyScrollRail = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  const railRef = useRef<HTMLDivElement | null>(null);
  const proxyRef = useRef<HTMLDivElement | null>(null);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [hasOverflow, setHasOverflow] = useState(false);

  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return;
    const measure = () => {
      setScrollWidth(rail.scrollWidth);
      setHasOverflow(rail.scrollWidth > rail.clientWidth + 1);
    };
    measure();
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(rail);
    const mutationObserver = new MutationObserver(measure);
    mutationObserver.observe(rail, { childList: true, subtree: true });
    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, []);

  const handleRailScroll = () => {
    const rail = railRef.current;
    const proxy = proxyRef.current;
    if (!rail || !proxy) return;
    if (Math.abs(proxy.scrollLeft - rail.scrollLeft) > 1) proxy.scrollLeft = rail.scrollLeft;
  };

  const handleProxyScroll = () => {
    const rail = railRef.current;
    const proxy = proxyRef.current;
    if (!rail || !proxy) return;
    if (Math.abs(rail.scrollLeft - proxy.scrollLeft) > 1) rail.scrollLeft = proxy.scrollLeft;
  };

  return (
    <>
      <div
        ref={railRef}
        onScroll={handleRailScroll}
        className={cn(
          "overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          className
        )}
      >
        {children}
      </div>
      <div
        ref={proxyRef}
        onScroll={handleProxyScroll}
        className={cn(
          "sticky-scroll-proxy sticky bottom-[calc(60px+env(safe-area-inset-bottom))] z-20 shrink-0 overflow-x-auto md:bottom-0",
          !hasOverflow && "hidden"
        )}
      >
        <div className="h-px" style={{ width: scrollWidth }} />
      </div>
    </>
  );
};

export default StickyScrollRail;
